import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api, formatVnd, type Cape } from '../lib/api'

const HERO = 'https://accminecraft.net/image/cape/optifine_2.png'

function CapeOrderModal({ cape, onClose }: { cape: Cape; onClose: () => void }) {
  const { user, refresh } = useAuth()
  const nav = useNavigate()
  const [name, setName] = useState(user?.tennhanvat || '')
  const [email, setEmail] = useState('')
  const [pay, setPay] = useState<'bank' | 'xu'>('bank')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    setBusy(true)
    try {
      const r = await api.createCapeOrder({ cape_id: cape.id, character_name: name, email, pay_method: pay })
      if ('kind' in r.order && r.order.kind === 'queue') {
        await refresh()
        setDone(true)
      } else {
        nav(`/checkout/${r.order.id}`)
      }
    } catch (ex) {
      setErr((ex as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const short = pay === 'xu' && user != null && user.balance < cape.price

  return (
    <div className="amc-modal-backdrop" onClick={onClose}>
      <div className="amc-modal" role="dialog" aria-modal="true" aria-label="Đặt cape" onClick={(e) => e.stopPropagation()}>
        <h2>{done ? 'Đã đặt cape' : `Đặt ${cape.name}`}</h2>
        {done ? (
          <>
            <p>Shop sẽ giao trong tối đa 15 phút.</p>
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Đóng
              </button>
              <Link className="btn btn-primary" to="/account/dat-ho">
                Xem đơn đặt hộ
              </Link>
            </div>
          </>
        ) : (
          <form className="form-stack" onSubmit={submit}>
            <label>
              Tên nhân vật
              <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={32} autoFocus />
            </label>
            <label>
              Email đăng ký cape
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <fieldset className="paym">
              <legend>Thanh toán</legend>
              <label className={`paym-opt${pay === 'bank' ? ' active' : ''}`}>
                <input type="radio" name="cape_pay" checked={pay === 'bank'} onChange={() => setPay('bank')} />
                <i className="fa-solid fa-building-columns" aria-hidden />
                <span className="paym-label">Chuyển khoản</span>
                <strong className="paym-price">{formatVnd(cape.price_transfer)}</strong>
              </label>
              <label className={`paym-opt${pay === 'xu' ? ' active' : ''}`}>
                <input type="radio" name="cape_pay" checked={pay === 'xu'} onChange={() => setPay('xu')} />
                <i className="fa-solid fa-coins" aria-hidden />
                <span className="paym-label">
                  Ví xu
                  {user ? (
                    <small className={short ? 'paym-short' : undefined}>
                      Số dư {user.balance.toLocaleString('vi-VN')}
                      {short ? (
                        <>
                          {' · '}
                          <Link to="/deposit?tab=card">Nạp thêm</Link>
                        </>
                      ) : null}
                    </small>
                  ) : null}
                </span>
                <strong className="paym-price">{formatVnd(cape.price)}</strong>
              </label>
            </fieldset>
            {err ? <p className="note">{err}</p> : null}
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Hủy
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || short}>
                {busy ? 'Đang đặt…' : 'Đặt cape'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export function CapePage() {
  const { user } = useAuth()
  const nav = useNavigate()
  const [items, setItems] = useState<Cape[]>([])
  const [err, setErr] = useState('')
  const [pick, setPick] = useState<Cape | null>(null)

  useEffect(() => {
    api
      .capes()
      .then((r) => setItems(r.capes))
      .catch((e) => setErr(e.message))
  }, [])

  return (
    <div className="cape">
      <section className="cape-hero">
        <div>
          <h1 className="page-title">Áo choàng OptiFine Minecraft</h1>
          <p className="page-lead">{items.length ? `${items.length} mẫu đang bán` : 'Đang tải…'}</p>
        </div>
        <img src={HERO} alt="Nhân vật mặc áo choàng OptiFine" loading="lazy" />
      </section>
      {err ? <p className="note">{err}</p> : null}
      <div className="cape-grid">
        {items.map((c) => (
          <article key={c.id} className="cape-card">
            <div className="cape-media">
              {c.image_url ? <img src={c.image_url} alt={c.name} loading="lazy" /> : <i className="fa-solid fa-shirt" aria-hidden />}
              {c.featured ? <span className="cape-hot">Nổi bật</span> : null}
            </div>
            <h3>{c.name}</h3>
            {c.category ? <p className="muted cape-mod">Mod: {c.category}</p> : null}
            <p className="cape-price">
              <strong>{formatVnd(c.price_transfer)}</strong>
              <span className="muted">
                <i className="fa-solid fa-coins" aria-hidden /> {c.price.toLocaleString('vi-VN')} xu
              </span>
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => (user ? setPick(c) : nav('/login', { state: { from: '/cape' } }))}
            >
              <i className="fa-solid fa-cart-shopping" aria-hidden /> Đặt cape
            </button>
          </article>
        ))}
      </div>
      {pick ? <CapeOrderModal cape={pick} onClose={() => setPick(null)} /> : null}
    </div>
  )
}
